import {
  removeCleanOrNotDefined,
  useCreateGcuPage,
  useCreateLegalNoticesPage,
  useCreatePrivacyPolicyPage,
  useGcuFormData,
  useLegalNoticesFormData,
  usePrivacyPolicyFormData,
  useUpdateGcuPage,
  useUpdateLegalNoticesPage,
  useUpdatePrivacyPolicyPage,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import {
  GcuForm,
  LegalNoticesForm,
  LegalNoticesFormProps,
  PrivacyPolicyForm,
} from "@/components/cms/PoliciesForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import {
  cmsPageLocaleBaseSchemaShape,
  gcuContentSchema,
  legalNoticesContentSchema,
  privacyPolicyContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  isCmsLocaleSectionDirty,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const legalNoticesSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      legalNoticesContentSchema.omit({
        id: true,
      }),
    ),
  ),
);

const gcuSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(gcuContentSchema.omit({ id: true })),
  ),
);

const privacyPolicySchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      privacyPolicyContentSchema.omit({ id: true }),
    ),
  ),
);

const formSchema = z.object({
  legalNotices: legalNoticesSchema,
  gcu: gcuSchema,
  privacyPolicy: privacyPolicySchema,
});

type FormData = z.infer<typeof formSchema>;

const PoliciesCMS = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();

  // `isSuccess` sert de garde anti-doublon : tant qu'une requête n'a pas
  // répondu (ou a échoué), on ne sait pas si la page existe déjà en base et un
  // `create` à l'aveugle ajoute une n-ième CmsPage pour le même (type, langue).
  const { data: legalNoticesData, isSuccess: legalNoticesLoaded } =
    useLegalNoticesFormData();
  const { data: gcuData, isSuccess: gcuLoaded } = useGcuFormData();
  const { data: privacyPolicyData, isSuccess: privacyPolicyLoaded } =
    usePrivacyPolicyFormData();

  const policiesLoaded =
    legalNoticesLoaded && gcuLoaded && privacyPolicyLoaded;

  const createLegalNotices = useCreateLegalNoticesPage();
  const updateLegalNotices = useUpdateLegalNoticesPage();

  const createGcu = useCreateGcuPage();
  const updateGcu = useUpdateGcuPage();

  const createPrivacyPolicy = useCreatePrivacyPolicyPage();
  const updatePrivacyPolicy = useUpdatePrivacyPolicyPage();

  const { toast } = useToast();

  const defaultLegalNoticesValues = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const legalNotices = legalNoticesData?.[lang];
        return {
          published: legalNotices?.published ?? false,
          pageName: legalNotices?.pageName ?? "",
          ...pageThemeFromDto(legalNotices),
          content: legalNotices?.content?.content,
        };
      }, websiteLanguages),
    [legalNoticesData, websiteLanguages],
  );

  const defaultGcuValues = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const gcu = gcuData?.[lang];
        return {
          published: gcu?.published ?? false,
          pageName: gcu?.pageName ?? "",
          ...pageThemeFromDto(gcu),
          content: gcu?.content?.content,
        };
      }, websiteLanguages),
    [gcuData, websiteLanguages],
  );

  const defaultPrivacyPolicyValues = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const privacyPolicy = privacyPolicyData?.[lang];
        return {
          published: privacyPolicy?.published ?? false,
          pageName: privacyPolicy?.pageName ?? "",
          ...pageThemeFromDto(privacyPolicy),
          content: privacyPolicy?.content?.content,
        };
      }, websiteLanguages),
    [privacyPolicyData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      legalNotices: defaultLegalNoticesValues,
      gcu: defaultGcuValues,
      privacyPolicy: defaultPrivacyPolicyValues,
    },
  });

  const hasInitLegalNotices = useRef(false);
  const hasInitGcu = useRef(false);
  const hasInitPrivacyPolicy = useRef(false);

  useEffect(() => {
    if (legalNoticesData != null && !hasInitLegalNotices.current && Object.keys(defaultLegalNoticesValues).length > 0) {
      form.reset({
        ...form.getValues(),
        legalNotices: defaultLegalNoticesValues,
      });
      hasInitLegalNotices.current = true;
    }
  }, [form, defaultLegalNoticesValues, legalNoticesData]);

  useEffect(() => {
    if (gcuData != null && !hasInitGcu.current && Object.keys(defaultGcuValues).length > 0) {
      form.reset({
        ...form.getValues(),
        gcu: defaultGcuValues,
      });
      hasInitGcu.current = true;
    }
  }, [form, defaultGcuValues, gcuData]);

  useEffect(() => {
    if (privacyPolicyData != null && !hasInitPrivacyPolicy.current && Object.keys(defaultPrivacyPolicyValues).length > 0) {
      form.reset({
        ...form.getValues(),
        privacyPolicy: defaultPrivacyPolicyValues,
      });
      hasInitPrivacyPolicy.current = true;
    }
  }, [form, defaultPrivacyPolicyValues, privacyPolicyData]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(submittedData.legalNotices).forEach((key) => {
      const localeData = submittedData.legalNotices[key as SupportedLocale];
      const baseData = legalNoticesData?.[key as SupportedLocale];
      if (
        !localeData ||
        !isCmsLocaleSectionDirty(form, `legalNotices.${key}`)
      )
        return;

      const existingId = baseData?.content.id;
      if (!existingId && !legalNoticesLoaded) return;

      const mutation = existingId ? updateLegalNotices : createLegalNotices;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate({
        id: existingId ?? "",
        lang: key as SupportedLocale,
        data: buildCmsPagePatchPayload({
          pageName: pageNameStr,
          published,
          pageBackgroundColorId,
          pageTextColorMode,
          pageTextColorId,
          content,
        }),
      });
    });

    Object.keys(submittedData.privacyPolicy).forEach((key) => {
      const localeData = submittedData.privacyPolicy[key as SupportedLocale];
      const baseData = privacyPolicyData?.[key as SupportedLocale];
      if (
        !localeData ||
        !isCmsLocaleSectionDirty(form, `privacyPolicy.${key}`)
      )
        return;

      const existingId = baseData?.content.id;
      if (!existingId && !privacyPolicyLoaded) return;

      const mutation = existingId ? updatePrivacyPolicy : createPrivacyPolicy;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate({
        id: existingId ?? "",
        lang: key as SupportedLocale,
        data: buildCmsPagePatchPayload({
          pageName: pageNameStr,
          published,
          pageBackgroundColorId,
          pageTextColorMode,
          pageTextColorId,
          content,
        }),
      });
    });

    Object.keys(submittedData.gcu).forEach((key) => {
      const localeData = submittedData.gcu[key as SupportedLocale];
      const baseData = gcuData?.[key as SupportedLocale];
      if (
        !localeData ||
        !isCmsLocaleSectionDirty(form, `gcu.${key}`)
      )
        return;

      const existingId = baseData?.content.id;
      if (!existingId && !gcuLoaded) return;

      const mutation = existingId ? updateGcu : createGcu;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate({
        id: existingId ?? "",
        lang: key as SupportedLocale,
        data: buildCmsPagePatchPayload({
          pageName: pageNameStr,
          published,
          pageBackgroundColorId,
          pageTextColorMode,
          pageTextColorId,
          content,
        }),
      });
    });

    toast({
      title: t("success"),
      description: t("policiesPageSuccessMessage"),
      duration: 4000,
      variant: "success",
    });
  };

  const tabList = useMemo(
    () =>
      [
        {
          label: t("cms.policies.tabs.legalNotices"),
          component: LegalNoticesForm,
          value: "legal-notices",
          prepend: "legalNotices",
        },
        {
          label: t("cms.policies.tabs.gcu"),
          component: GcuForm,
          value: "gcu",
          prepend: "gcu",
        },
        {
          label: t("cms.policies.tabs.privacyPolicy"),
          component: PrivacyPolicyForm,
          value: "privacy-policy",
          prepend: "privacyPolicy",
        },
      ] satisfies {
        label: string;
        value: string;
        component: (props: LegalNoticesFormProps) => JSX.Element;
        prepend: string;
      }[],
    [t],
  );

  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, legalNoticesData, "legalNotices");
    removeCleanOrNotDefined(form, gcuData, "gcu");
    removeCleanOrNotDefined(form, privacyPolicyData, "privacyPolicy");
  };

  const [activeTab, setActiveTab] = useState(tabList[0]?.value ?? "legal-notices");
  const activeFormPrefix = tabList.find((tab) => tab.value === activeTab)?.prepend;

  return (
    <CMSLayout
      pageName={t("cmsTabPagesLegales")}
      formIds={["legal"]}
      isValid={policiesLoaded}
    >
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-policies">
        <CmsPageAppearanceConfig
          formId="legal"
          formPrefix={activeFormPrefix}
        />
        <Tabs
          className="w-full"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <form
            onSubmit={wrapHandleSubmitWithAction(
              form,
              cleanBeforeSubmit,
              onSubmit,
            )}
            id="legal"
            className="flex flex-col gap-4"
          >
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="flex w-full flex-col gap-6 data-[state=inactive]:hidden"
                >
                  <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${tab.prepend}.${lang}.published`}
                      />
                    )}
                    render={(lang) => (
                      <Comp formBase={`${tab.prepend}.${lang}`} />
                    )}
                  />
                </TabsContent>
              );
            })}
          </form>
        </Tabs>
              </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default PoliciesCMS;
